import { useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useEventsAdminDemo } from "@app/lib/events-admin-demo-context";
import { GET_SYSTEM_HEALTH } from "@app/graphql/events-admin.queries";
import {
  DownloadIcon,
  FilterIcon,
  TrendingIcon,
} from "@app/lib/events-admin-icons";
import { Button } from "@its-engineering-and-development/ui-foundation/components";

type AlertFilter = "all" | "Critical" | "Warning" | "Info";

function getMetricTone(status: string) {
  return status === "Warning"
    ? "events-tone events-tone--warning"
    : "events-tone events-tone--success";
}

function getAlertTone(severity: string) {
  if (severity === "Critical") {
    return "events-tone events-tone--critical";
  }

  if (severity === "Warning") {
    return "events-tone events-tone--warning";
  }

  return "events-tone events-tone--info";
}

export function DashboardPage() {
  const { systemHealth: localSystemHealth } = useEventsAdminDemo();

  // GraphQL: fetch system health from mock server
  const { data: gqlData } = useQuery<{
    systemHealth: typeof localSystemHealth;
  }>(GET_SYSTEM_HEALTH, { fetchPolicy: "network-only" });
  const systemHealth = gqlData?.systemHealth ?? localSystemHealth;

  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [alertFilter, setAlertFilter] = useState<AlertFilter>("all");
  const [refreshLabel, setRefreshLabel] = useState("Refresh Metrics");

  return (
    <section className="events-page events-page--stack">
      <header className="events-page__hero">
        <div>
          <p className="events-page__eyebrow">ITS / Super Admin</p>
          <h1 className="events-page__title">System Health Monitor</h1>
          <p className="events-page__subtitle">
            Real-time monitoring and diagnostics for platform infrastructure.
          </p>
        </div>
      </header>

      <div className="events-health-grid">
        {systemHealth.metrics.map((metric) => {
          const isExpanded = expandedCardId === metric.id;
          return (
            <article
              key={metric.id}
              className={`events-health-card ${isExpanded ? "is-expanded" : ""}`}
              onClick={() => setExpandedCardId(isExpanded ? null : metric.id)}
              style={{ cursor: "pointer" }}
            >
              <div className="events-health-card__head">
                <div className="events-health-card__icon">
                  <TrendingIcon className="size-5" />
                </div>
                <span className={getMetricTone(metric.status)}>
                  {metric.status}
                </span>
              </div>
              <h2>{metric.label}</h2>
              <p className="events-health-card__value">
                {metric.value} <span>target: {metric.target}</span>
              </p>
              <p className="events-health-card__helper">{metric.helper}</p>
              {isExpanded && (
                <dl className="events-health-card__details">
                  <div>
                    <dt>{metric.detailA}</dt>
                    <dd>{metric.detailAValue}</dd>
                  </div>
                  <div>
                    <dt>{metric.detailB}</dt>
                    <dd>{metric.detailBValue}</dd>
                  </div>
                  <div>
                    <dt>Last updated</dt>
                    <dd>Just now</dd>
                  </div>
                </dl>
              )}
            </article>
          );
        })}
      </div>

      <article className="events-surface events-surface--alerts">
        <header className="events-page__hero events-page__hero--table">
          <h2 className="events-surface__title">Recent Alerts</h2>
          <Button
            size="sm"
            color="secondary"
            className="events-outline-button"
            onPress={() => {
              const filters: AlertFilter[] = [
                "all",
                "Critical",
                "Warning",
                "Info",
              ];
              const currentIndex = filters.indexOf(alertFilter);
              setAlertFilter(filters[(currentIndex + 1) % filters.length]);
            }}
          >
            <FilterIcon className="size-4" />
            {alertFilter === "all" ? "Filter" : `Filter: ${alertFilter}`}
          </Button>
        </header>

        <ul className="events-alert-list">
          {systemHealth.alerts
            .filter(
              (alertItem) =>
                alertFilter === "all" || alertItem.severity === alertFilter,
            )
            .map((alertItem) => (
              <li key={alertItem.id}>
                <div>
                  <p className="events-alert-list__message">
                    {alertItem.message}
                  </p>
                  <p className="events-alert-list__meta">
                    {alertItem.service} <span>.</span>{" "}
                    <span className={getAlertTone(alertItem.status)}>
                      {alertItem.status}
                    </span>
                  </p>
                </div>
                <div className="events-alert-list__right">
                  <span>{alertItem.occurredAt}</span>
                  <span className={getAlertTone(alertItem.severity)}>
                    {alertItem.severity}
                  </span>
                </div>
              </li>
            ))}
        </ul>

        <a
          className="events-inline-link events-inline-link--center"
          href="#alerts"
        >
          View All Alerts
        </a>
      </article>

      <div className="events-page__hero-actions events-page__hero-actions--center">
        <Button
          size="lg"
          color="primary"
          className="events-solid-button"
          onPress={() => {
            setRefreshLabel("Refreshed!");
            setExpandedCardId(null);
            setTimeout(() => setRefreshLabel("Refresh Metrics"), 2000);
          }}
        >
          {refreshLabel}
        </Button>
        <Button
          size="lg"
          color="secondary"
          className="events-outline-button"
          onPress={() => {
            const csvRows = [
              ["Metric", "Value", "Target", "Status"].join(","),
              ...systemHealth.metrics.map((m) =>
                [m.label, m.value, m.target, m.status].join(","),
              ),
            ];
            const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "system-health-report.csv";
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          <DownloadIcon className="size-4" />
          Export Report
        </Button>
      </div>
    </section>
  );
}
